import { useMemo, useRef, useState } from 'react';

import { entityKind, entityName, fullTransform, type SceneDoc } from '../../lib/scene-doc.js';

/**
 * Top-down (XZ) schematic of the scene, drawn when the engine runtime is not
 * available, so the viewport still shows where things are and supports
 * click-to-select. Approximate by design: parent positions are added, parent
 * rotation and scale are ignored.
 */
export function SceneSchematic({
  doc,
  selection,
  onSelect,
}: {
  doc: SceneDoc;
  selection: readonly string[];
  onSelect: (ids: string[]) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null);

  const items = useMemo(() => {
    const byId = new Map(doc.entities.map((e) => [e.id, e]));
    const world = (id: string, depth = 0): [number, number, number] => {
      const e = byId.get(id);
      if (!e || depth > 32) return [0, 0, 0];
      const p = fullTransform(e.transform).position;
      if (!e.parent) return [p[0], p[1], p[2]];
      const pp = world(e.parent, depth + 1);
      return [p[0] + pp[0], p[1] + pp[1], p[2] + pp[2]];
    };
    // Entities at (nearly) the same spot stack their labels instead of overprinting.
    const slots = new Map<string, number>();
    return doc.entities.map((e) => {
      const t = fullTransform(e.transform);
      const pos = world(e.id);
      const key = `${Math.round(pos[0] * 2)}:${Math.round(pos[2] * 2)}`;
      const slot = slots.get(key) ?? 0;
      slots.set(key, slot + 1);
      const mesh = (e.components?.mesh ?? null) as { source?: string } | null;
      const isPlane = mesh?.source === 'primitive:plane';
      return {
        entity: e,
        kind: entityKind(e),
        pos,
        slot,
        footprint: isPlane ? [t.scale[0], t.scale[2]] : null,
      };
    });
  }, [doc]);

  // Fit the content into view (ignoring huge planes so props stay readable).
  const fit = useMemo(() => {
    const pts = items.filter((i) => !i.footprint).map((i) => [i.pos[0], i.pos[2]] as const);
    if (!pts.length) return { cx: 0, cz: 0, span: 20 };
    const xs = pts.map((p) => p[0]);
    const zs = pts.map((p) => p[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minZ = Math.min(...zs);
    const maxZ = Math.max(...zs);
    return { cx: (minX + maxX) / 2, cz: (minZ + maxZ) / 2, span: Math.max(8, (maxX - minX) * 1.6, (maxZ - minZ) * 1.6) };
  }, [items]);

  const span = fit.span / zoom;
  const vb = `${fit.cx - span / 2 + pan.x} ${fit.cz - span / 2 + pan.y} ${span} ${span}`;
  const unit = span / 100;
  const gridStep = span > 200 ? 50 : span > 60 ? 10 : span > 15 ? 5 : 1;
  const gridLines = [];
  const start = (v: number) => Math.floor(v / gridStep) * gridStep;
  for (let x = start(fit.cx + pan.x - span); x <= fit.cx + pan.x + span; x += gridStep) {
    gridLines.push(<line key={`x${x}`} x1={x} x2={x} y1={fit.cz + pan.y - span} y2={fit.cz + pan.y + span} className={x === 0 ? 'axis-z' : 'grid-line'} />);
  }
  for (let z = start(fit.cz + pan.y - span); z <= fit.cz + pan.y + span; z += gridStep) {
    gridLines.push(<line key={`z${z}`} y1={z} y2={z} x1={fit.cx + pan.x - span} x2={fit.cx + pan.x + span} className={z === 0 ? 'axis-x' : 'grid-line'} />);
  }

  return (
    <svg
      className="schematic"
      viewBox={vb}
      preserveAspectRatio="xMidYMid meet"
      onWheel={(e) => setZoom((z) => Math.min(20, Math.max(0.05, z * (e.deltaY < 0 ? 1.15 : 1 / 1.15))))}
      onPointerDown={(e) => {
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
        drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y, moved: false };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const box = (e.currentTarget as Element).getBoundingClientRect();
        const scale = span / Math.min(box.width, box.height);
        const dx = e.clientX - d.x;
        const dy = e.clientY - d.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
        if (d.moved) setPan({ x: d.px - dx * scale, y: d.py - dy * scale });
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        if (d && !d.moved && (e.target as Element).tagName === 'svg') onSelect([]);
      }}
      strokeWidth={unit * 0.15}
    >
      <g>{gridLines}</g>
      {items
        .filter((i) => i.footprint)
        .map((i) => (
          <rect
            key={i.entity.id}
            className={`schem-plane${selection.includes(i.entity.id) ? ' is-selected' : ''}`}
            x={i.pos[0] - i.footprint![0]! / 2}
            y={i.pos[2] - i.footprint![1]! / 2}
            width={i.footprint![0]}
            height={i.footprint![1]}
            onPointerUp={(e) => {
              if (!drag.current?.moved) {
                e.stopPropagation();
                onSelect([i.entity.id]);
              }
            }}
          />
        ))}
      {items
        .filter((i) => !i.footprint)
        .map((i) => {
          const selected = selection.includes(i.entity.id);
          return (
            <g
              key={i.entity.id}
              className={`schem-item kind-${i.kind}${selected ? ' is-selected' : ''}`}
              transform={`translate(${i.pos[0]} ${i.pos[2]})`}
              onPointerUp={(e) => {
                e.stopPropagation();
                if (drag.current?.moved) return;
                drag.current = null;
                onSelect([i.entity.id]);
              }}
            >
              {selected && <circle r={unit * 3.2} className="schem-halo" />}
              {i.kind === 'light' ? (
                <polygon points={star(unit * 2)} />
              ) : i.kind === 'camera' ? (
                <polygon points={`${-unit * 2},${-unit * 1.4} ${unit * 2},${-unit * 1.4} ${unit * 1.2},${unit * 1.6} ${-unit * 1.2},${unit * 1.6}`} />
              ) : i.kind === 'empty' ? (
                <circle r={unit * 1.1} />
              ) : (
                <rect x={-unit * 1.6} y={-unit * 1.6} width={unit * 3.2} height={unit * 3.2} rx={unit * 0.4} />
              )}
              <text y={unit * (4.6 + i.slot * 2.8)} fontSize={unit * 2.3} textAnchor="middle">
                {entityName(i.entity)}
              </text>
            </g>
          );
        })}
    </svg>
  );
}

function star(r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 16; i += 1) {
    const a = (i / 16) * Math.PI * 2;
    const rr = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${Math.cos(a) * rr},${Math.sin(a) * rr}`);
  }
  return pts.join(' ');
}
